import { useMemo } from 'react';
import {
  AlertCircle,
  TrendingDown,
  AlertTriangle,
  Activity,
  TrendingUp,
  CheckCircle,
  Loader2,
  RefreshCw,
  Package,
  ShieldAlert,
  BarChart3,
} from 'lucide-react';
import { useProducts } from '../shared/hooks/useProducts';
import { useSettings } from '../shared/hooks/useSettings';
import { ProductWithCalculations, StatusType } from '../shared/types/inventory';
import { DonutChart } from '../components/charts/DonutChart';
import { HorizontalBarChart } from '../components/charts/HorizontalBarChart';
import { CoverageGauge } from '../components/charts/CoverageGauge';
import { MiniSparkBar } from '../components/charts/MiniSparkBar';

const STATUS_PALETTE: Record<StatusType, string> = {
  ruptura: '#dc2626',
  iminente: '#ea580c',
  minimo: '#ca8a04',
  critica: '#0891b2',
  excesso: '#2563eb',
  saudavel: '#16a34a',
};

const STATUS_META: Record<StatusType, { label: string; icon: typeof AlertCircle; bgClass: string; textClass: string }> = {
  ruptura: { label: 'Ruptura Instalada', icon: AlertCircle, bgClass: 'bg-red-50 border-red-100', textClass: 'text-red-700' },
  iminente: { label: 'Ruptura Iminente', icon: TrendingDown, bgClass: 'bg-orange-50 border-orange-100', textClass: 'text-orange-700' },
  minimo: { label: 'Perto do Minimo', icon: AlertTriangle, bgClass: 'bg-yellow-50 border-yellow-100', textClass: 'text-yellow-700' },
  critica: { label: 'Cobertura Critica', icon: Activity, bgClass: 'bg-cyan-50 border-cyan-100', textClass: 'text-cyan-700' },
  excesso: { label: 'Excesso', icon: TrendingUp, bgClass: 'bg-blue-50 border-blue-100', textClass: 'text-blue-700' },
  saudavel: { label: 'Saudavel', icon: CheckCircle, bgClass: 'bg-green-50 border-green-100', textClass: 'text-green-700' },
};

const STATUS_ORDER: StatusType[] = ['ruptura', 'iminente', 'minimo', 'critica', 'excesso', 'saudavel'];

function computeAnalytics(products: ProductWithCalculations[]) {
  const counts: Record<StatusType, number> = {
    ruptura: 0, iminente: 0, minimo: 0, critica: 0, excesso: 0, saudavel: 0,
  };
  let totalStock = 0;
  let totalTarget = 0;
  let totalSuggested = 0;
  let coverageSum = 0;
  let coverageCount = 0;

  for (const p of products) {
    counts[p.status]++;
    totalStock += p.estoqueAtual;
    totalTarget += p.estoqueAlvo;
    totalSuggested += p.pedidoSugerido;
    if (p.cmd > 0) {
      coverageSum += p.cobertura;
      coverageCount++;
    }
  }

  const avgCoverage = coverageCount > 0 ? coverageSum / coverageCount : 0;
  const alertCount = counts.ruptura + counts.iminente + counts.minimo;
  const healthPct = products.length > 0 ? ((counts.saudavel / products.length) * 100) : 0;
  const fillRate = totalTarget > 0 ? (totalStock / totalTarget) * 100 : 0;

  const topLowCoverage = [...products]
    .filter((p) => p.cmd > 0)
    .sort((a, b) => a.cobertura - b.cobertura)
    .slice(0, 8);

  const topHighOrder = [...products]
    .filter((p) => p.pedidoSugerido > 0)
    .sort((a, b) => b.pedidoSugerido - a.pedidoSugerido)
    .slice(0, 8);

  return {
    counts,
    totalStock,
    totalTarget,
    totalSuggested,
    avgCoverage,
    alertCount,
    healthPct,
    fillRate,
    topLowCoverage,
    topHighOrder,
  };
}

export const DashboardPage = () => {
  const { settings } = useSettings();
  const { products, loading, error, refetch } = useProducts();

  const analytics = useMemo(() => computeAnalytics(products), [products]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
          <p className="text-gray-500 text-sm">Carregando dados do estoque...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-gray-900 font-semibold mb-1">Erro ao carregar dados</p>
            <p className="text-gray-500 text-sm">{error}</p>
          </div>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const donutSegments = STATUS_ORDER.map((s) => ({
    label: STATUS_META[s].label,
    value: analytics.counts[s],
    color: STATUS_PALETTE[s],
  }));

  const sparkValues = STATUS_ORDER.map((s) => ({
    label: STATUS_META[s].label,
    value: analytics.counts[s],
    color: STATUS_PALETTE[s],
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Visão Geral do Estoque</h1>
          <p className="text-gray-500 text-sm">
            {products.length} produtos monitorados &middot; Lead time {settings.leadTime}d &middot; Cobertura alvo {settings.diasCoberturaAlvo}d
          </p>
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Total de Produtos"
          value={String(products.length)}
          icon={<Package className="w-5 h-5" />}
          accentClass="text-gray-600 bg-gray-50"
        />
        <KPICard
          label="Produtos em Alerta"
          value={String(analytics.alertCount)}
          icon={<ShieldAlert className="w-5 h-5" />}
          accentClass="text-red-600 bg-red-50"
          subtitle={`${products.length > 0 ? ((analytics.alertCount / products.length) * 100).toFixed(0) : 0}% do total`}
        />
        <KPICard
          label="Saúde do Estoque"
          value={`${analytics.healthPct.toFixed(0)}%`}
          icon={<CheckCircle className="w-5 h-5" />}
          accentClass={analytics.healthPct >= 60 ? 'text-green-600 bg-green-50' : 'text-orange-600 bg-orange-50'}
          subtitle="produtos saudáveis"
        />
        <KPICard
          label="Pedidos Sugeridos"
          value={analytics.totalSuggested.toLocaleString('pt-BR')}
          icon={<BarChart3 className="w-5 h-5" />}
          accentClass="text-teal-600 bg-teal-50"
          subtitle="unidades a repor"
        />
      </div>

      {/* Stacked bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Distribuição por Status</h3>
        <MiniSparkBar values={sparkValues} />
      </div>

      {/* Status Cards + Donut */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {STATUS_ORDER.map((status) => {
              const meta = STATUS_META[status];
              const Icon = meta.icon;
              const count = analytics.counts[status];
              const pct = products.length > 0 ? ((count / products.length) * 100).toFixed(0) : '0';

              return (
                <div
                  key={status}
                  className={`rounded-xl border p-4 transition-shadow hover:shadow-md ${meta.bgClass}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-1.5 rounded-lg bg-white/70 ${meta.textClass}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      {meta.label}
                    </span>
                  </div>
                  <div className="flex items-end justify-between">
                    <span className={`text-3xl font-bold ${meta.textClass}`}>{count}</span>
                    <span className="text-xs text-gray-500 mb-1">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col items-center justify-center">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 self-start">Composição do Estoque</h3>
          <DonutChart segments={donutSegments} size={200} strokeWidth={28} />
        </div>
      </div>

      {/* Coverage Gauges */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-5">Indicadores de Cobertura</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <CoverageGauge
            label="Cobertura Média"
            value={analytics.avgCoverage}
            max={settings.diasCoberturaAlvo * 2}
            target={settings.diasCoberturaAlvo}
            color={analytics.avgCoverage >= settings.diasCoberturaAlvo ? '#16a34a' : '#ea580c'}
            unit=" dias"
          />
          <CoverageGauge
            label="Taxa de Preenchimento"
            value={analytics.fillRate}
            max={120}
            target={100}
            color={analytics.fillRate >= 80 ? '#16a34a' : analytics.fillRate >= 50 ? '#ca8a04' : '#dc2626'}
            unit="%"
          />
          <CoverageGauge
            label="Lead Time"
            value={settings.leadTime}
            max={30}
            target={settings.leadTime}
            color="#0891b2"
            unit=" dias"
          />
        </div>
      </div>

      {/* Two column charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lowest coverage */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Menor Cobertura (dias)</h3>
          <p className="text-xs text-gray-400 mb-4">Produtos com menor dias de cobertura</p>
          {analytics.topLowCoverage.length > 0 ? (
            <HorizontalBarChart
              items={analytics.topLowCoverage.map((p) => ({
                label: p.nome.length > 30 ? p.nome.slice(0, 30) + '...' : p.nome,
                value: p.cobertura,
                color: p.cobertura < settings.leadTime
                  ? '#dc2626'
                  : p.cobertura < settings.diasCoberturaAlvo
                    ? '#ca8a04'
                    : '#16a34a',
              }))}
              formatValue={(v) => `${v.toFixed(1)}d`}
            />
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">Sem dados de cobertura disponíveis</p>
          )}
        </div>

        {/* Highest suggested order */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Maiores Pedidos Sugeridos</h3>
          <p className="text-xs text-gray-400 mb-4">Produtos que mais precisam de reposição</p>
          {analytics.topHighOrder.length > 0 ? (
            <HorizontalBarChart
              items={analytics.topHighOrder.map((p) => ({
                label: p.nome.length > 30 ? p.nome.slice(0, 30) + '...' : p.nome,
                value: p.pedidoSugerido,
                color: STATUS_PALETTE[p.status],
              }))}
              formatValue={(v) => v.toLocaleString('pt-BR') + ' un'}
            />
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">Nenhum pedido sugerido</p>
          )}
        </div>
      </div>
    </div>
  );
};

interface KPICardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  accentClass: string;
  subtitle?: string;
}

function KPICard({ label, value, icon, accentClass, subtitle }: KPICardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-start gap-3">
      <div className={`p-2.5 rounded-lg flex-shrink-0 ${accentClass}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-900 leading-tight mt-0.5">{value}</p>
        {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}
